import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { FormInstance } from 'antd'
import { Form } from 'antd'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { fetchFloorplans } from '@/api/floorplans.api'
import { fetchDeskNumbers } from '@/api/counters.api'

export interface CounterFormValues {
    id?: string
    deskNumber: number
    name: string
    unitId: string
    unitName?: string
    diagramId: string
    diagramName?: string
}

interface CounterFormProps {
    form: FormInstance<CounterFormValues>
    mode: 'create' | 'update'
    showUnitColumn: boolean
    isOpen: boolean
    counterId: string | null
    defaultUnitId?: string
    onSubmit: (values: CounterFormValues) => void
}

const PILL_INPUT = '!bg-white !text-[#1E2F41] disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

const DESK_NUMBER_OPTIONS = Array.from({ length: 100 }, (_, index) => {
    const value = index + 1
    return { value, label: String(value).padStart(2, '0') }
})

export function CounterForm({ form, mode, showUnitColumn, isOpen, counterId, defaultUnitId, onSubmit }: CounterFormProps) {
    const selectedUnitId = Form.useWatch('unitId', form)
    const unitId = selectedUnitId || (!showUnitColumn ? defaultUnitId : undefined)

    const { data: usedDesks = [], isLoading: isLoadingDesks, isError: isDeskError } = useQuery({
        queryKey: ['counter-form-used-desks', unitId],
        queryFn: async ({ signal }) => {
            const desks = []
            let page = 0
            let totalPages: number
            do {
                const response = await fetchDeskNumbers(page, 100, { unitId }, signal)
                desks.push(...response.items)
                totalPages = response.totalPages
                page += 1
            } while (page < totalPages)
            return desks
        },
        enabled: isOpen && !!unitId,
        staleTime: 0,
        gcTime: 0,
    })
    const occupiedNumbers = new Set(
        usedDesks.filter((desk) => desk.id !== counterId).map((desk) => Number(desk.deskNumber)),
    )
    const availableDeskOptions = DESK_NUMBER_OPTIONS.filter(
        (option) => !occupiedNumbers.has(option.value),
    )

    const loadUnits = useCallback(async ({ page, signal, searchText }: any) => {
        const response = await fetchUnits(page, 10, { keySearch: searchText?.trim() || undefined, status: 1 }, signal)
        const items = Array.isArray(response.items) ? response.items : []
        const hasNextPage = items.length >= 10
        return {
            options: items.map((unit: any) => ({ value: unit.id, label: unit.name })),
            hasNextPage,
            nextPage: hasNextPage ? page + 1 : undefined,
        }
    }, [])

    const loadDiagrams = useCallback(async ({ page, signal, searchText }: any) => {
        const currentUnitId = form.getFieldValue('unitId');
        const response = await fetchFloorplans(page, 10, {
            keySearch: searchText?.trim() || undefined,
            unitId: currentUnitId || undefined
        }, signal)

        const items = Array.isArray(response.items) ? response.items : []
        const hasNextPage = items.length >= 10
        return {
            options: items.map((diagram: any) => ({ value: diagram.id, label: diagram.name })),
            hasNextPage,
            nextPage: hasNextPage ? page + 1 : undefined,
        }
    }, [form])

    const handleFinish = (values: CounterFormValues) => {
        const trimmedValues = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        ) as CounterFormValues
        onSubmit(trimmedValues)
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={(label, info) => <>{label}{info.required && <span className="text-destructive ml-1">*</span>}</>}
            className="space-y-6"
        >
            {showUnitColumn && (
                <Form.Item name="unitId" label="Đơn vị" rules={[{ required: true, message: 'Vui lòng chọn đơn vị' }]}>
                    <Select
                        cacheKey="counter-units-select"
                        initialPage={0}
                        lazy
                        size="large"
                        placeholder="Chọn đơn vị"
                        allowClear
                        className="w-full h-10 !text-[#1E2F41] [&_input]:!text-[#1E2F41]"
                        enableRemoteSearch
                        initialOptions={mode === 'update' && form.getFieldValue('unitId') ? [{ value: form.getFieldValue('unitId'), label: form.getFieldValue('unitName') }] : []}
                        loadOptions={loadUnits}
                        onLazyValueChange={(nextUnitId) => form.setFieldsValue({ unitId: nextUnitId, diagramId: undefined, deskNumber: undefined })}
                    />
                </Form.Item>
            )}

            <Form.Item name="diagramId" label="Sơ đồ" rules={[{ required: true, message: 'Vui lòng chọn sơ đồ' }]}>
                <Select
                    cacheKey={`counter-diagrams-select-${selectedUnitId || 'all'}`}
                    initialPage={0}
                    lazy
                    size="large"
                    placeholder="Chọn sơ đồ"
                    allowClear
                    className="w-full h-10 !text-[#1E2F41] [&_input]:!text-[#1E2F41]"
                    enableRemoteSearch
                    initialOptions={mode === 'update' && form.getFieldValue('diagramName') ? [{ value: form.getFieldValue('diagramId'), label: form.getFieldValue('diagramName') }] : []}
                    loadOptions={loadDiagrams}
                    onLazyValueChange={(diagramId) => form.setFieldsValue({ diagramId })}
                />
            </Form.Item>

            <Form.Item name="deskNumber" label="Số quầy" rules={[{ required: true, message: 'Vui lòng chọn số quầy' }]}>
                <Select
                    size="large"
                    placeholder="Chọn số quầy"
                    disabled={!unitId || isLoadingDesks || isDeskError}
                    loading={isLoadingDesks}
                    className="w-full h-10 !text-[#1E2F41] [&_input]:!text-[#1E2F41]"
                    options={availableDeskOptions}
                    onValueChange={(val) => form.setFieldsValue({ deskNumber: val })}
                />
            </Form.Item>

            <Form.Item name="name" label="Tên quầy" rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập tên quầy' }]}>
                <Input placeholder="Nhập tên quầy" size="large" className={PILL_INPUT} />
            </Form.Item>
        </Form>
    )
}
